"use client";

import { useEffect, useRef, useState } from "react";
import { LANGUAGES } from "@/lib/data/languages";
import { useLocale } from "@/lib/context/LocaleProvider";
import { cn } from "@/lib/utils";
import { ChevronDown, Globe } from "@/components/ui/icons";

/** Header language switcher with a click-away dropdown. */
export function LanguageMenu({ locked = false }: { locked?: boolean }) {
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const [wasLocked, setWasLocked] = useState(locked);
  const ref = useRef<HTMLDivElement>(null);

  // Collapse a menu that was open when the header locked, so unlocking does not
  // pop it back up. Adjusting state during render beats an extra effect pass.
  if (wasLocked !== locked) {
    setWasLocked(locked);
    if (locked) setOpen(false);
  }

  const current = LANGUAGES.find((l) => l.code === locale) ?? LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);


  if (locked) {
    return (
      <div
        className="h-8 w-[72px] rounded-full bg-[rgba(26,24,22,0.06)]"
        aria-hidden
      />
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={current.name}
        aria-expanded={open}
        className="flex items-center gap-[7px] rounded-full px-[13px] py-[7px] text-[13px] font-semibold text-ink transition-colors hover:bg-[rgba(26,24,22,0.05)]"
      >
        <Globe size={17} />
        <span>{current.short}</span>
        <ChevronDown
          size={14}
          className={cn("transition-transform duration-200", open && "rotate-180")}
        />
      </button>

      {open ? (
        <div className="menu-pop absolute right-0 top-[calc(100%_+_6px)] z-[70] max-h-[280px] min-w-[210px] overflow-y-auto">
          {LANGUAGES.map((lang) => {
            const active = lang.code === locale;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  setLocale(lang.code);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-[11px] px-2.5 py-[9px] text-left text-[13.5px] font-medium text-ink transition-colors hover:bg-[rgba(26,24,22,0.05)]",
                  active && "bg-[rgba(26,24,22,0.05)]",
                )}
              >
                <span className="flex-1">{lang.name}</span>
                <span className="text-[12px] font-semibold uppercase tracking-[0.4px] text-ink-3">
                  {lang.short}
                </span>
                <span
                  className={cn(
                    "w-3.5 text-[13px] font-bold text-accent",
                    active ? "opacity-100" : "opacity-0",
                  )}
                >
                  ✓
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
